import { DbEnum } from "@nexus/types/core"
import { createSlice } from "@reduxjs/toolkit"

import type { OptimisticMessage } from "@/common/types/chat"

import * as reducers from "./embedReducers"
import { shouldHideEmbedMessage } from "./embedReducers/messages"
import { adoptReopenedEmbedChat, clearClosedEmbedChat } from "./embedReducers/reopenedChat"
import { createInitialEmbedState } from "./embedState"
import { initializeEmbedChat } from "./embedThunks/initializeEmbedChat"
import { loadEmbedMessages } from "./embedThunks/loadMessages"
import { loadMoreEmbedMessages } from "./embedThunks/loadMoreMessages"
import { reopenClosedEmbedChat } from "./embedThunks/reopenClosedEmbedChat"
import { sendEmbedMessage } from "./embedThunks/sendMessage"
import { stopEmbedMessage } from "./embedThunks/stopMessage"

/**
 * Embed chat slice
 */
export const embedChatSlice = createSlice({
  name: "embedChat",
  initialState: createInitialEmbedState(),
  reducers: {
    openEmbedChat: reducers.openEmbedChat,
    closeEmbedChat: reducers.closeEmbedChat,
    setActiveEmbedChat: reducers.setActiveEmbedChat,
    addEmbedMessage: reducers.addEmbedMessage,
    setEmbedMessages: reducers.setEmbedMessages,
    addEmbedMessageFromSocket: reducers.addEmbedMessageFromSocket,
    addEmbedStreamingToken: reducers.addEmbedStreamingToken,
    completeEmbedStreamingMessage: reducers.completeEmbedStreamingMessage,
    clearEmbedStreamingMessage: reducers.clearEmbedStreamingMessage,
    embedStreamingError: reducers.embedStreamingError,
    addEmbedStreamingBlock: reducers.addEmbedStreamingBlock,
    updateEmbedStreamingBlock: reducers.updateEmbedStreamingBlock,
    completeEmbedStreamingBlock: reducers.completeEmbedStreamingBlock,
    completeEmbedCodeInterpreterStreaming: reducers.completeEmbedCodeInterpreterStreaming,
    stopEmbedSending: reducers.stopEmbedSending,
    updateEmbedInitPhase: reducers.updateEmbedInitPhase,
    setEmbedInitError: reducers.setEmbedInitError,
    updateEmbedMetadata: reducers.updateEmbedMetadata,
    resetEmbedSession: reducers.resetEmbedSession,
    removeEmbedSession: reducers.removeEmbedSession,
    updateEmbedChatTopic: reducers.updateEmbedChatTopic
  },
  extraReducers: (builder) => {
    builder
      // initializeEmbedChat
      .addCase(initializeEmbedChat.pending, (state, action) => {
        const { deploymentId, sessionId, isPreview } = action.meta.arg
        const tempChatId = `temp_${sessionId}`

        if (!state.chats[tempChatId]) {
          state.chats[tempChatId] = {
            id: tempChatId,
            integrationId: deploymentId,
            sessionId,
            messages: [],
            isLoadingMessages: true,
            streamingMessages: {},
            isSendingMessage: false,
            shouldShowDynamicWhitespace: false,
            unreadCount: 0,
            initializationPhase: "fetching-chatroom",
            initializationError: null,
            isPreview,
            isNewChatroom: false,
            lastAccessedAt: Date.now(),
            topic: null,
            hasMoreHistory: false,
            isLoadingMoreMessages: false
          }
        }

        state.chats[tempChatId].initializationPhase = "fetching-chatroom"
        state.activeChatId = tempChatId
      })
      .addCase(initializeEmbedChat.fulfilled, (state, action) => {
        const {
          chatId,
          deploymentId,
          sessionId,
          isPreview,
          isNewChatroom,
          deployment,
          messages,
          hasMoreHistory,
          topic
        } = action.payload
        const tempChatId = `temp_${sessionId}`

        // Remove temp chat if exists
        if (state.chats[tempChatId]) {
          delete state.chats[tempChatId]
        }

        state.chats[chatId] = {
          id: chatId,
          integrationId: deploymentId,
          sessionId,
          messages: messages.filter((m) => !shouldHideEmbedMessage(m)),
          isLoadingMessages: false,
          streamingMessages: {},
          isSendingMessage: false,
          shouldShowDynamicWhitespace: false,
          unreadCount: 0,
          initializationPhase: "ready",
          initializationError: null,
          isPreview,
          isNewChatroom,
          deployment,
          lastAccessedAt: Date.now(),
          topic,
          hasMoreHistory,
          isLoadingMoreMessages: false
        }

        state.activeChatId = chatId
      })
      .addCase(initializeEmbedChat.rejected, (state, action) => {
        const { sessionId } = action.meta.arg
        const tempChatId = `temp_${sessionId}`
        const chat = state.chats[tempChatId]

        if (chat) {
          chat.initializationPhase = "error"
          chat.initializationError =
            action.payload?.message || action.error.message || "Failed to initialize"
          chat.isLoadingMessages = false
        }
      })

      // reopenClosedEmbedChat — the conversation was deleted under an open tab
      .addCase(reopenClosedEmbedChat.pending, clearClosedEmbedChat)
      .addCase(reopenClosedEmbedChat.fulfilled, adoptReopenedEmbedChat)

      // sendEmbedMessage
      .addCase(sendEmbedMessage.pending, (state, action) => {
        const { chatId, content, messageId, files = [], images = [] } = action.meta.arg
        const chat = state.chats[chatId]

        if (!chat) return

        chat.isSendingMessage = true
        chat.shouldShowDynamicWhitespace = true

        // Build optimistic files array for display
        const optimisticFiles = files.map((f) => ({
          id: f.id,
          name: f.name,
          textContent: null,
          urls: f.url ? [f.url] : [],
          urlAWS: {},
          size: f.size ? String(f.size) : "0",
          type: f.name.includes(".")
            ? f.name.split(".").pop()?.toUpperCase() || "UNKNOWN"
            : "UNKNOWN",
          status: "DONE",
          category: "Document",
          from: "CHAT",
          isFolder: false,
          metadata: { mimeType: f.mimeType || "" }
        }))

        // Optimistic message - minimal fields for UI, replaced by backend response
        const userMessage: OptimisticMessage = {
          id: messageId,
          chatId,
          content: {
            text: content,
            ...(images.length > 0 && { images })
          },
          type: DbEnum.MessageContentType.HUMAN,
          status: DbEnum.MessageStatus.PROCESSED,
          createdAt: new Date(),
          updatedAt: new Date(),
          files: optimisticFiles.length > 0 ? optimisticFiles : undefined,
          textContent: content
        }

        const existingIndex = chat.messages.findIndex((m) => m.id === messageId)
        if (existingIndex === -1) {
          chat.messages.push(userMessage)
        }
      })
      .addCase(sendEmbedMessage.fulfilled, (state, action) => {
        const { chatId, wasNewChatroom } = action.payload
        const chat = state.chats[chatId]

        if (!chat) return

        // Do NOT clear isSendingMessage here. This fires when the POST of the
        // user's message is acked by the backend, NOT when the agent finishes
        // responding. Clearing it now re-enables the input while the agent is
        // still thinking / a skill/RPA is running, letting the customer
        // re-send and re-trigger the skill multiple times. It is cleared by
        // the streaming COMPLETED / error / stop handlers instead.

        // After first message, chatroom is no longer "new"
        if (wasNewChatroom) {
          chat.isNewChatroom = false
        }
      })
      .addCase(sendEmbedMessage.rejected, (state, action) => {
        const { chatId } = action.meta.arg
        const chat = state.chats[chatId]

        if (chat) {
          chat.isSendingMessage = false
          chat.shouldShowDynamicWhitespace = false
        }
      })

      // loadEmbedMessages
      .addCase(loadEmbedMessages.pending, (state, action) => {
        const { chatId } = action.meta.arg
        const chat = state.chats[chatId]

        if (chat) {
          chat.isLoadingMessages = true
        }
      })
      .addCase(loadEmbedMessages.fulfilled, (state, action) => {
        const { chatId, messages } = action.payload
        const chat = state.chats[chatId]

        if (!chat) return

        chat.messages = messages.filter((m) => !shouldHideEmbedMessage(m))
        chat.isLoadingMessages = false
      })
      .addCase(loadEmbedMessages.rejected, (state, action) => {
        const { chatId } = action.meta.arg
        const chat = state.chats[chatId]

        if (chat) {
          chat.isLoadingMessages = false
        }
      })
      // loadMoreEmbedMessages
      .addCase(loadMoreEmbedMessages.pending, (state, action) => {
        const { chatId } = action.meta.arg
        const chat = state.chats[chatId]

        if (chat) {
          chat.isLoadingMoreMessages = true
        }
      })
      .addCase(loadMoreEmbedMessages.fulfilled, (state, action) => {
        const { chatId, messages, hasMore } = action.payload
        const chat = state.chats[chatId]

        if (!chat) return

        const existingIds = new Set(chat.messages.map((m) => m.id))
        const newMessages = messages.filter(
          (m) => !existingIds.has(m.id) && !shouldHideEmbedMessage(m)
        )

        chat.messages = [...newMessages, ...chat.messages]
        chat.hasMoreHistory = hasMore
        chat.isLoadingMoreMessages = false
      })
      .addCase(loadMoreEmbedMessages.rejected, (state, action) => {
        const { chatId } = action.meta.arg
        const chat = state.chats[chatId]

        if (chat) {
          chat.isLoadingMoreMessages = false
        }
      })

      // stopEmbedMessage
      .addCase(stopEmbedMessage.pending, (state, action) => {
        const { chatId } = action.meta.arg
        const chat = state.chats[chatId]

        if (chat) {
          // Immediate UI feedback - stop showing as sending
          chat.isSendingMessage = false
          chat.shouldShowDynamicWhitespace = false
          chat.streamingMessages = {}
        }
      })
      .addCase(stopEmbedMessage.fulfilled, (state, action) => {
        const { chatId } = action.payload
        const chat = state.chats[chatId]

        if (chat) {
          chat.isSendingMessage = false
          chat.shouldShowDynamicWhitespace = false
        }
      })
      .addCase(stopEmbedMessage.rejected, (state, action) => {
        const { chatId } = action.meta.arg
        const chat = state.chats[chatId]

        if (chat) {
          // Even on error, stop the UI state
          chat.isSendingMessage = false
          chat.shouldShowDynamicWhitespace = false
        }
      })
  }
})

export const embedChatReducer = embedChatSlice.reducer
export const embedChatActions = embedChatSlice.actions
